import {FC, useEffect, useMemo} from 'react';
import {usePersona} from 'src/permissions/hooks';
import {usersApi} from 'src/api';
import {AMPAutocomplete} from 'src/components/common/AMPAutocomplete';
import {FormikProps, useFormikContext} from 'formik';
import {AMPAutocompleteProps} from 'src/components/common/AMPAutocomplete/AMPAutocomplete.types';
import {getHelperText} from 'src/utils/forms';
import {useIntl} from 'react-intl';
import {CreateRfidFormFromType} from '..';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import {UserTypeEnum} from 'src/enums/common';

export const PrivateDriverForRfidDropdown: FC<{ id: number | null; index: number }> = ({
                                                                                           id,
                                                                                           index,
                                                                                       }) => {
    const intl = useIntl();
    const persona = usePersona();

    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    const isBusinessUser = persona.isBackOffice;
    const {setFieldValue, handleBlur, touched, errors, values} =
        useFormikContext<CreateRfidFormFromType>();

    // The business being worked in, for everyone. Sending it only for a back-office user with a
    // business of their own left the platform administrator choosing from every tenant's drivers.
    const privateDriverRequestBody = () => ({
        userTypes: [UserTypeEnum.PRIVATE_DRIVER],
        businessId,
    });

    const {data: privateDriverData, refetch: privateDriverRefetch} = usersApi.useGetUsersQuery({
        params: {
            page: 0,
            size: 1_000,
        },
        data: privateDriverRequestBody(),
    });

    const options = useMemo(() => {
        return (
            privateDriverData?.content?.map((driver) => {
                const label =
                    (driver?.firstName + driver?.lastName)?.length > 0
                        ? `${driver?.firstName} ${driver?.lastName}`
                        : '';

                return {
                    id: Number(driver?.id) ?? 0,
                    label,
                };
            }) ?? []
        );
    }, [privateDriverData?.content]);

    const handleChangePrivateDriverValue = (
        setFieldValue: FormikProps<CreateRfidFormFromType>['setFieldValue']
    ): AMPAutocompleteProps['onChange'] => {
        return (_event, newValue) => {
            const drivers = values['drivers'] || [];

            // Handle clearing the field (when X button is clicked)
            if (!newValue) {
                if (drivers.length === 0) {
                    setFieldValue('drivers', [{id: undefined}]);
                    return;
                }

                const currentDriver = drivers[index] || {id: undefined};
                const updatedDriver = {...currentDriver, id: undefined};
                const updatedDrivers = [...drivers];
                updatedDrivers[index] = updatedDriver;
                setFieldValue('drivers', updatedDrivers);
                return;
            }

            // Handle selecting a new value
            if (drivers.length === 0) {
                setFieldValue('drivers', [{id: newValue.id}]);
                return;
            }

            const currentDriver = drivers[index] || {id: undefined};
            const updatedDriver = {...currentDriver, id: newValue.id};
            const updatedDrivers = [...drivers];
            updatedDrivers[index] = updatedDriver;
            setFieldValue('drivers', updatedDrivers);
        };
    };

    useEffect(() => {
        privateDriverRefetch();
    }, []);

    return (
        <AMPAutocomplete
            options={options}
            value={options.find((item) => item.id === id)}
            label={index === 0 ? 'privateDriverTabForRfidDialog' : ''}
            input={'driverId'}
            showAvatar
            onChange={handleChangePrivateDriverValue(setFieldValue)}
            onBlur={handleBlur}
            hasError={
                (touched?.['drivers'] && Boolean(errors['drivers'])) ||
                (touched?.['drivers'] && values['drivers']?.length === 0)
            }
            helperText={getHelperText('driverId', touched, errors, intl)}
        />
    );
};
